import { rest } from "msw";
import type { UserInterfaceConfigurationAdapterType } from "../adapters/UserInterfaceConfiguration.ts";
import type { DomainConfig } from "../config.ts";
import {
  UserInterfaceConfigurationSchema,
  UserInterfaceConfigurationScope,
  UserInterfaceConfigurationStatus,
  type UserInterfaceConfigurationType,
  UserInterfaceConfigurationVisibility,
} from "../schemas/UserInterfaceConfigurationsSchemas.ts";
import { Resources, type GetListResponseType } from "../util/index.ts";
import {
  getApiUrlWithEndpoint,
  type BuildMockHandlersType,
} from "./handlers.ts";
import { DomainFaker, DomainMock } from "./utils.ts";

export const UserInterfaceConfigurationMock = new DomainMock<
  UserInterfaceConfigurationType,
  typeof UserInterfaceConfigurationSchema
>(
  UserInterfaceConfigurationSchema,
  () => {
    const typeProp = DomainFaker.en.helpers.arrayElement([
      "table",
      "chart",
      "dashboard",
    ]);

    return {
      id: DomainFaker.en.string.uuid(),
      company_id: DomainFaker.en.string.uuid(),
      owner_id: DomainFaker.en.string.uuid(),
      name: DomainFaker.en.word.words(),
      description: DomainFaker.en.lorem.sentences(2),
      scope: DomainFaker.en.helpers.arrayElement(
        Object.values(UserInterfaceConfigurationScope)
      ),
      type: typeProp,
      key: [
        DomainFaker.en.helpers.arrayElement(Object.values(Resources)),
        DomainFaker.en.word.verb({ strategy: "shortest" }),
      ],
      configuration:
        {
          table: { columns: [{ key: "col1", label: "Column 1" }] },
          chart: { chartType: "line" },
          dashboard: { layout: "grid" },
        }[typeProp] ?? {},
      status: DomainFaker.en.helpers.arrayElement(
        Object.values(UserInterfaceConfigurationStatus)
      ),
      visibility: DomainFaker.en.helpers.arrayElement(
        Object.values(UserInterfaceConfigurationVisibility)
      ),
      is_default: DomainFaker.en.helpers.arrayElement([true, false]),
      created_at: DomainFaker.en.date.past().toISOString(),
      created_by: DomainFaker.en.string.uuid(),
      updated_at: DomainFaker.en.date.past().toISOString(),
      updated_by: DomainFaker.en.string.uuid(),
    };
  },
  true
).fix();

export const buildUserInterfaceConfigurationHandlers: BuildMockHandlersType<
  UserInterfaceConfigurationAdapterType
> = (configData: DomainConfig) => {
  const { nodeURL: url } = configData;

  return {
    getList: rest.get<
      never,
      {
        page: string;
        pageSize: string;
      },
      GetListResponseType<UserInterfaceConfigurationType>
    >(
      getApiUrlWithEndpoint({
        url,
        version: 1,
        endpoint: "/user_interface_configurations",
      }),
      (req, res, ctx) => {
        const page = Number(req.url.searchParams.get("page"));
        const pageSize = Number(req.url.searchParams.get("pageSize"));
        const data = new Array(30)
          .fill(0)
          .map(() => UserInterfaceConfigurationMock.generate());

        return res(
          ctx.status(200),
          ctx.json({
            data,
            page_number: page,
            page_size: pageSize,
            total_entries: data.length,
            total_pages: 1,
          })
        );
      }
    ),
    getOne: rest.get(
      getApiUrlWithEndpoint({
        url,
        version: 1,
        endpoint: "/user_interface_configurations/:id",
      }),
      (req, res, ctx) => {
        const { id } = req.params;
        const mockData = UserInterfaceConfigurationMock.mock;

        if (typeof id !== "string") {
          return res(ctx.status(400));
        }

        mockData.id = id;

        return res(ctx.status(200), ctx.json(mockData));
      }
    ),
  };
};
